import { Component, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { NotificationService } from '../../service/common/notification/notification.service';
import { RoutingService } from '../../service/common/routing/routing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogService } from '../../service/common/confirm-dialog/confirm-dialog.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { KaskadenKontakt } from '../../domain/model/kaskaden-kontakt';
import { Adresse } from '../../domain/model/adresse';
import { mergeMap, take } from 'rxjs/operators';
import { NgForm } from '@angular/forms';
import { TnbUser } from '../../service/auth/tnb-user';
import { lastValueFrom, Observable } from 'rxjs';
import { Kundenbetreuer } from '../../domain/model/kundenbetreuer';
import { KundenbetreuerService } from '../../service/kundenbetreuer/kundenbetreuer.service';
import { Schaltleitung } from '../../domain/model/schaltleitung';
import { SchaltleitungService } from '../../service/schaltleitung/schaltleitung.service';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { WestnetzSidenavService } from '../../service/common/westnetz-sidenav/westnetz-sidenav.service';

@Component({
    selector: 'tnb-netzbetreiber-detail',
    templateUrl: './netzbetreiber-detail.component.html',
    styleUrls: [
        './netzbetreiber-detail.component.scss',
    ],
    standalone: false,
})
export class NetzbetreiberDetailComponent {
    private authService = inject(AuthenticationService);
    routingService = inject(RoutingService);
    private confirmDialogService = inject(ConfirmDialogService);
    private notificationService = inject(NotificationService);
    private activeRoute = inject(ActivatedRoute);
    private kundenbetreuerService = inject(KundenbetreuerService);
    private router = inject(Router);
    private schaltleitungService = inject(SchaltleitungService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private westnetzSidenavService = inject(WestnetzSidenavService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    public readonly EMAIL_PATTERN = '(?:[a-z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"' +
        '(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")' +
        '@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}' +
        '(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\\])';
    private readonly DEFAULT_TEILGEBIET_VALUE = 'Stammgebiet';

    @ViewChild('netzbetreiberForm', { static: false }) public netzbetreiberForm: NgForm;

    public netzbetreiber: Netzbetreiber;
    public openedInSideNav = false;
    public netzbetreiberType: string = null;
    public originalNetzbetreiber = Netzbetreiber.createEmptyNetzbetreiber();
    public stadtwerkeId: string;
    public createNewMode = false;
    public westnetzAdmin = false;
    private pgonChangedConfirmed = false;

    public emailKommunikationInputFieldIndex = 1;

    public availableKundenbetreuer: Observable<Kundenbetreuer[]>;
    public availableSchaltleitungen: Observable<Schaltleitung[]>;
    public kundenbetreuerReadonly = '';
    public schaltleitungenReadonly = '';

    constructor() {
        this.openedInSideNav = this.activeRoute.snapshot.queryParams['openedInSideNav'] === 'true';
        this.stadtwerkeId = this.activeRoute.snapshot.queryParams['stadtwerkeId'];
        this.createNewMode = this.activeRoute.snapshot.queryParams['createnew'] === 'true';

        this.authService.user$.pipe(take(1)).subscribe(
            (profile: TnbUser) => {
                this.netzbetreiberType = profile.netzbetreiberType;
                this.westnetzAdmin = this.authService.isWestnetzAdmin(profile);

                if (this.createNewMode) {
                    if (this.westnetzAdmin) {
                        this.availableKundenbetreuer = this.kundenbetreuerService.getAllKundenbetreuer();
                        this.availableSchaltleitungen = this.schaltleitungService.getSchaltleitungen();
                        this.netzbetreiber = Netzbetreiber.createEmptyNetzbetreiber();
                        this.netzbetreiberService.getAvailableStadtwerkeId()
                            .pipe(take(1))
                            .subscribe(stadtwerkeId => {
                                this.netzbetreiber.stadtwerkeId = stadtwerkeId;
                                this.netzbetreiber.teilgebiet = this.DEFAULT_TEILGEBIET_VALUE;
                                this.changeDetectorRef.markForCheck();
                            });
                    } else {
                        this.closeSideNav();
                    }
                } else {
                    this.availableKundenbetreuer = this.kundenbetreuerService.getAllKundenbetreuer();
                    this.availableSchaltleitungen = this.schaltleitungService.getSchaltleitungen();
                    this.reloadNetzbetreiber(this.stadtwerkeId);

                }
                this.changeDetectorRef.markForCheck();
            },
        );
    }

    private reloadNetzbetreiber(stadtwerkeId: string) {
        this.netzbetreiberService.getNetzbetreiber(stadtwerkeId)
            .pipe(take(1))
            .subscribe(nb => {
                this.netzbetreiber = nb;
                this.kundenbetreuerReadonly = (this.netzbetreiber.kundenbetreuerList.length > 0) ?
                    this.netzbetreiber.kundenbetreuerList.map(function (kb) {
                        return kb.nachname + ', ' + kb.vorname;
                    }).join('/') :
                    'Kein Kundenbetreuer zugewiesen';

                this.schaltleitungenReadonly = (this.netzbetreiber.schaltleitungen.length > 0) ?
                    this.netzbetreiber.schaltleitungen.map(function (kb) {
                        return kb.name;
                    }).join('/') :
                    'Keine Schaltleitung(en) zugewiesen';

                this.originalNetzbetreiber = JSON.parse(JSON.stringify(this.netzbetreiber));
                if (!this.netzbetreiber.kaskadenKontakt) {
                    this.netzbetreiber.kaskadenKontakt = new KaskadenKontakt();
                }
                if (!this.netzbetreiber.adresse) {
                    this.netzbetreiber.adresse = new Adresse();
                }
                if (this.netzbetreiber.kaskadenKontakt.emailKommunikation4) {
                    this.emailKommunikationInputFieldIndex = 4;
                } else if (this.netzbetreiber.kaskadenKontakt.emailKommunikation3) {
                    this.emailKommunikationInputFieldIndex = 3;
                } else if (this.netzbetreiber.kaskadenKontakt.emailKommunikation2) {
                    this.emailKommunikationInputFieldIndex = 2;
                }
                this.changeDetectorRef.markForCheck();
            });
    }

    public isSaveButtonDisabled(): boolean {
        return !this.netzbetreiberForm || !this.netzbetreiberForm.form.valid || this.netzbetreiberForm.form.pristine;
    }

    public createNetzbetreiber() {
        this.netzbetreiberService.createNetzbetreiber(this.createSendableNetzbetreiber())
            .pipe(take(1))
            .subscribe(
                _nb => {
                    this.notificationService.success('Der Netzbetreiber wurde erstellt');
                    this.netzbetreiberService.refreshRequested$.next();
                    this.closeSideNav();
                },
            );
    }

    public saveBasisDaten() {
        this.netzbetreiberService.saveNetzbetreiberBasisdaten(this.createSendableNetzbetreiber())
            .pipe(take(1))
            .subscribe(() => {
                this.notificationService.success('Der Netzbetreiber wurde gespeichert');
                this.netzbetreiberService.refreshRequested$.next();
                this.closeSideNav();
            });
    }

    public saveContactData() {
        // clean up emails from emailKommunikation to avoid sending empty strings and gaps
        const emails: string[] = [];
        if (this.netzbetreiber.kaskadenKontakt.emailKommunikation2) {
            emails.push(this.netzbetreiber.kaskadenKontakt.emailKommunikation2);
        }
        if (this.netzbetreiber.kaskadenKontakt.emailKommunikation3) {
            emails.push(this.netzbetreiber.kaskadenKontakt.emailKommunikation3);
        }
        if (this.netzbetreiber.kaskadenKontakt.emailKommunikation4) {
            emails.push(this.netzbetreiber.kaskadenKontakt.emailKommunikation4);
        }
        if (emails[0]) {
            this.netzbetreiber.kaskadenKontakt.emailKommunikation2 = emails[0];
        } else {
            this.netzbetreiber.kaskadenKontakt.emailKommunikation2 = null;
        }
        if (emails[1]) {
            this.netzbetreiber.kaskadenKontakt.emailKommunikation3 = emails[1];
        } else {
            this.netzbetreiber.kaskadenKontakt.emailKommunikation3 = null;
        }
        if (emails[2]) {
            this.netzbetreiber.kaskadenKontakt.emailKommunikation4 = emails[2];
        } else {
            this.netzbetreiber.kaskadenKontakt.emailKommunikation4 = null;
        }

        this.netzbetreiberService.saveNetzbetreiberKaskadenKontakt(this.netzbetreiber)
            .pipe(
                mergeMap(() => this.netzbetreiberService.saveNetzbetreiberAdresse(this.netzbetreiber, false)),
                take(1),
            )
            .subscribe(() => {
                this.notificationService.success('Ihre Kontaktdaten wurden gespeichert');
                this.router.navigateByUrl(this.routingService.dashboard());
            });
    }

    public compareKundenbetreuer(k1: Kundenbetreuer, k2: Kundenbetreuer): boolean {
        return k1.id === k2.id;
    }

    public compareSchaltleitungen(s1: Schaltleitung, s2: Schaltleitung): boolean {
        return s1.name === s2.name && s1.kuerzel === s2.kuerzel;
    }

    public isDeleteButtonDisabled(): boolean {
        return this.netzbetreiber.datenAbfrageAktiv || !this.isSaveButtonDisabled();
    }

    public async deleteNetzbetreiber() {
        if (await this.confirmDialogService.confirm(
            'Sind Sie sich sicher, dass Sie diesen Netzbetreiber löschen möchten? ' +
            'Diese Aktion kann nicht rückgängig gemacht werden.',
            'Netzbetreiber löschen',
            'warn',
            'Ja, Netzbetreiber mit allen Daten löschen',
            'Abbrechen',
            'innogy_picto_bin_c-check',
        )) {
            await lastValueFrom(this.netzbetreiberService.deleteNetzbetreiber(this.netzbetreiber));
            this.notificationService.success('Ausgewählter Netzbetreiber wurde gelöscht.');
            this.netzbetreiberService.refreshRequested$.next();
            this.closeSideNav();
        }
    }

    // removes objects that would render the NB invalid at the backend
    private createSendableNetzbetreiber(): Netzbetreiber {
        const netzbetreiberToBeSent = JSON.parse(JSON.stringify(this.netzbetreiber));
        delete netzbetreiberToBeSent.kaskadenKontakt;
        delete netzbetreiberToBeSent.adresse;
        return netzbetreiberToBeSent;
    }

    public resetForm() {
        this.pgonChangedConfirmed = false;
        this.reloadNetzbetreiber(this.stadtwerkeId);
        this.netzbetreiberForm.form.markAsPristine();
    }

    public get isWestnetzUser(): boolean {
        return this.netzbetreiberType === 'WESTNETZ';
    }

    public get isWestnetzAdmin(): boolean {
        return this.westnetzAdmin;
    }

    public get isStadtwerkUser(): boolean {
        return this.netzbetreiberType === 'STADTWERK';
    }

    public async netzbetreibernummerChanged() {
        if (!this.createNewMode && !this.pgonChangedConfirmed) {
            if (await this.confirmDialogService.confirm(
                'Sind Sie sich sicher, dass Sie die Netzbetreibernummer (PGON) ändern/löschen möchten? ' +
                'Es kann sein, dass ein oder mehrere Benutzer ihren Zugriff verlieren.',
                'Achtung',
                'warn',
                'Ja, Netzbetreibernummer (PGON) ändern',
                'Abbrechen',
                'innogy_picto_c-check',
            )) {
                this.pgonChangedConfirmed = true;
            } else {
                this.netzbetreiber.netzbetreibernummer = this.originalNetzbetreiber.netzbetreibernummer;
            }
        }
    }

    getDeletedButtonTooltipText(): string {
        if (this.netzbetreiber.datenAbfrageAktiv) {
            return 'Teilnehmer der aktiven Datenabfrage können nicht gelöscht werden';
        } else {
            return '';
        }
    }

    protected closeSideNav(): void {
        this.westnetzSidenavService.closeRightnav();
        this.router.navigate([
            this.routingService.netzbetreiberPath(),
        ], {
            queryParams: { stadtwerkeId: null, openedInSideNav: null, createnew: null },
            queryParamsHandling: 'merge',
        });
    }
}
